import {
  ChangeEvent,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { ReactComponent as SearchIcon } from 'src/assets/svg/searchIconGray.svg'
import { ReactComponent as FilterIcon } from 'src/pages/AdAnalysis/AdLibrary/Details/FiltersBar/filter.svg'
import { ReactComponent as ChevronDownIcon } from 'src/assets/svg/chevron.svg'
import { ReactComponent as CheckboxInactiveIcon } from 'src/assets/svg/checkbox-inactive.svg'
import { ReactComponent as DoubleCheckIcon } from 'src/assets/svg/double-check-white.svg'
import { WarningBanner } from 'src/components/ProjectsWidget/CreateProjectModal/WarningBanner'
import styles from './campaignSourceSelector.module.scss'
import {
  CampaignItem,
  CampaignSourceRow,
  SourceId,
} from './interfaces/campaignSource.interface'
import {
  getCampaignUnitLabel,
  getSourceEntityLabel,
} from './useCampaignSources'

interface CampaignSourceSelectorProps {
  activeSourceId?: SourceId
  alreadyLinkedCampaignIds?: Set<string>
  campaignSources: CampaignSourceRow[]
  isCompact?: boolean
  isOpen: boolean
  measureContainerRef?: RefObject<HTMLElement>
  onToggleCampaign: (campaignId: string) => void
  onToggleSourceCampaigns: (campaigns: CampaignItem[]) => void
  selectedCampaignIds: string[]
  showWarningBanner?: boolean
}

const EMPTY_LINKED_IDS: Set<string> = new Set()
const SOURCES_LIST_COLLAPSED_HEIGHT = 290
const SOURCES_LIST_MAX_EXPANDED_HEIGHT = 460
const MODAL_CARD_MAX_HEIGHT = 740
const MODAL_VIEWPORT_OFFSET = 48

// Each expanded source renders its own card list, so the scroll-edge fade state
// lives per-list rather than in the parent. Fades only the edges that actually
// have content past them — a list short enough not to scroll is never dimmed.
const ScrollFadeList: FC<{ children: ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [hasAbove, setHasAbove] = useState(false)
  const [hasBelow, setHasBelow] = useState(false)

  const sync = useCallback(() => {
    const el = ref.current
    if (!el) return

    const { clientHeight, scrollHeight, scrollTop } = el
    setHasAbove(scrollTop > 1)
    // -1 absorbs sub-pixel rounding at the very bottom.
    setHasBelow(scrollTop + clientHeight < scrollHeight - 1)
  }, [])

  // Re-measure when the rendered rows change: search and the selected-only
  // filter both change how many cards are in the list.
  useEffect(() => {
    sync()
  }, [children, sync])

  return (
    <div
      className={classNames(styles.campaignCardsList, {
        [styles.fadeBoth]: hasAbove && hasBelow,
        [styles.fadeBottom]: !hasAbove && hasBelow,
        [styles.fadeTop]: hasAbove && !hasBelow,
      })}
      onScroll={sync}
      ref={ref}
    >
      {children}
    </div>
  )
}

export const CampaignSourceSelector: FC<CampaignSourceSelectorProps> = ({
  activeSourceId,
  alreadyLinkedCampaignIds = EMPTY_LINKED_IDS,
  campaignSources,
  isCompact = false,
  isOpen,
  measureContainerRef,
  onToggleCampaign,
  onToggleSourceCampaigns,
  selectedCampaignIds,
  showWarningBanner = false,
}) => {
  const [campaignSearchValue, setCampaignSearchValue] = useState<string>('')
  const [isSelectedOnlyFilterActive, setIsSelectedOnlyFilterActive] =
    useState<boolean>(false)
  const [expandedSourceIds, setExpandedSourceIds] = useState<string[]>(
    activeSourceId ? [activeSourceId] : []
  )
  const [sourcesListHeight, setSourcesListHeight] = useState<number>(
    SOURCES_LIST_COLLAPSED_HEIGHT
  )
  const sourcesListRef = useRef<HTMLDivElement>(null)

  const handleCampaignSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCampaignSearchValue(event.target.value)
  }

  const normalizedCampaignSearchValue = campaignSearchValue.trim().toLowerCase()
  const campaignSourcesWithSearch = campaignSources.map((source) => {
    const visibleCampaigns = normalizedCampaignSearchValue
      ? source.campaigns.filter((campaign) =>
          `${campaign.title} ${campaign.category}`
            .toLowerCase()
            .includes(normalizedCampaignSearchValue)
        )
      : source.campaigns
    const filteredCampaigns = isSelectedOnlyFilterActive
      ? visibleCampaigns.filter(({ id }) => selectedCampaignIds.includes(id))
      : visibleCampaigns

    return {
      ...source,
      visibleCampaigns: filteredCampaigns,
    }
  })

  useEffect(() => {
    if (isOpen) {
      setCampaignSearchValue('')
      setExpandedSourceIds(activeSourceId ? [activeSourceId] : [])
      setIsSelectedOnlyFilterActive(false)
      setSourcesListHeight(SOURCES_LIST_COLLAPSED_HEIGHT)
    }
  }, [activeSourceId, isOpen])

  // The list grows to fit its expanded rows but must never push the host modal
  // past the viewport, so the cap is derived from the modal's own measured
  // non-list height rather than a fixed number.
  useLayoutEffect(() => {
    if (!isOpen || isCompact) {
      return
    }

    const element = sourcesListRef.current
    const modalCard = measureContainerRef?.current

    if (!element || !modalCard) {
      return
    }

    if (expandedSourceIds.length === 0) {
      setSourcesListHeight(SOURCES_LIST_COLLAPSED_HEIGHT)
      return
    }

    const frameId = window.requestAnimationFrame(() => {
      const modalMaxHeight = Math.min(
        MODAL_CARD_MAX_HEIGHT,
        window.innerHeight - MODAL_VIEWPORT_OFFSET
      )
      const nonListHeight = modalCard.scrollHeight - element.clientHeight
      const measuredCap = modalMaxHeight - nonListHeight
      const viewportCap = Math.max(
        SOURCES_LIST_COLLAPSED_HEIGHT,
        Math.min(SOURCES_LIST_MAX_EXPANDED_HEIGHT, measuredCap)
      )
      setSourcesListHeight(Math.min(element.scrollHeight, viewportCap))
    })

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [
    campaignSourcesWithSearch,
    expandedSourceIds,
    isCompact,
    isOpen,
    measureContainerRef,
    selectedCampaignIds,
  ])

  return (
    <div
      className={`${styles.stepTwoContent} ${
        isCompact ? styles.stepTwoContentEditCampaign : ''
      }`}
    >
      {showWarningBanner ? (
        <WarningBanner
          text="Your project will be empty without campaigns. Select at least one campaign to see analytics data in your project dashboard."
          variant="neutral"
        />
      ) : null}

      <div className={styles.searchAndFilterRow}>
        <div className={styles.searchBox}>
          <SearchIcon className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            onChange={handleCampaignSearchChange}
            placeholder="Search campaigns..."
            type="text"
            value={campaignSearchValue}
          />
        </div>

        <button
          className={`${styles.selectedOnlyButton} ${
            isSelectedOnlyFilterActive ? styles.selectedOnlyButtonActive : ''
          }`}
          onClick={() => setIsSelectedOnlyFilterActive((prev) => !prev)}
          type="button"
        >
          <FilterIcon className={styles.filterIcon} />
          <span>Selected only</span>
        </button>
      </div>

      <div
        className={`${styles.sourcesList} ${
          isCompact ? styles.sourcesListEditCampaign : ''
        }`}
        ref={sourcesListRef}
        style={isCompact ? undefined : { height: `${sourcesListHeight}px` }}
      >
        {campaignSourcesWithSearch.map((source) => {
          const isSourceLocked =
            Boolean(activeSourceId) && source.id !== activeSourceId
          const isExpanded =
            !isSourceLocked && expandedSourceIds.includes(source.id)
          const SourceIcon = source.icon
          const { visibleCampaigns } = source
          const hasVisibleCampaigns = visibleCampaigns.length > 0
          const selectedCount = source.campaigns.filter(({ id }) =>
            selectedCampaignIds.includes(id)
          ).length
          const alreadyLinkedCount = source.campaigns.filter(({ id }) =>
            alreadyLinkedCampaignIds.has(id)
          ).length
          const selectableVisibleCampaigns = visibleCampaigns.filter(
            ({ id }) => !alreadyLinkedCampaignIds.has(id)
          )
          const areAllSourceCampaignsSelected =
            selectableVisibleCampaigns.length > 0 &&
            selectableVisibleCampaigns.every(({ id }) =>
              selectedCampaignIds.includes(id)
            )
          return (
            <div
              className={`${styles.sourceItem} ${
                hasVisibleCampaigns || source.id === activeSourceId
                  ? ''
                  : styles.sourceItemHidden
              }`}
              key={source.id}
            >
              <div className={styles.sourceItemInner}>
                <button
                  className={`${styles.socialListeningRow} ${
                    isExpanded ? styles.socialListeningRowExpanded : ''
                  } ${isSourceLocked ? styles.socialListeningRowDisabled : ''}`}
                  disabled={isSourceLocked}
                  onClick={() =>
                    setExpandedSourceIds((prev) =>
                      prev.includes(source.id)
                        ? prev.filter((id) => id !== source.id)
                        : [...prev, source.id]
                    )
                  }
                  type="button"
                >
                  <div className={styles.socialListeningLeft}>
                    <SourceIcon
                      className={styles.socialListeningIcon}
                      style={{ color: source.color }}
                    />
                    <span className={styles.socialListeningLabel}>
                      {source.label}
                    </span>
                  </div>

                  <div className={styles.socialListeningRight}>
                    <span className={styles.campaignCount}>
                      {visibleCampaigns.length}{' '}
                      {getSourceEntityLabel(source.id, visibleCampaigns.length)}
                    </span>
                    <div
                      className={`${styles.selectedPill} ${
                        selectedCount > 0 ? styles.selectedPillActive : ''
                      }`}
                    >
                      {selectedCount} selected
                    </div>
                    {alreadyLinkedCount > 0 && (
                      <div className={styles.alreadyAddedPill}>
                        {alreadyLinkedCount} added
                      </div>
                    )}
                    <ChevronDownIcon
                      className={`${styles.dropdownIcon} ${
                        isExpanded ? styles.dropdownIconExpanded : ''
                      }`}
                    />
                  </div>
                </button>

                <div
                  className={`${styles.sourceDropdownPanel} ${
                    isExpanded ? styles.sourceDropdownPanelExpanded : ''
                  }`}
                >
                  <div className={styles.sourceDropdownInner}>
                    <button
                      className={`${styles.selectAllRow} ${
                        selectableVisibleCampaigns.length === 0
                          ? styles.selectAllRowDisabled
                          : ''
                      }`}
                      onClick={() => onToggleSourceCampaigns(visibleCampaigns)}
                      type="button"
                    >
                      {areAllSourceCampaignsSelected ? (
                        <span className={styles.selectedCheckbox}>
                          <DoubleCheckIcon
                            className={styles.selectedCheckboxIcon}
                          />
                        </span>
                      ) : (
                        <CheckboxInactiveIcon
                          className={styles.campaignCheckboxIcon}
                        />
                      )}
                      <span>Select All</span>
                      <span className={styles.selectAllDot} />
                      <span className={styles.selectAllCount}>
                        {selectableVisibleCampaigns.length}
                      </span>
                    </button>

                    <ScrollFadeList>
                      {visibleCampaigns.map((campaign) => {
                        const isCampaignSelected = selectedCampaignIds.includes(
                          campaign.id
                        )
                        const isAlreadyLinked = alreadyLinkedCampaignIds.has(
                          campaign.id
                        )
                        return (
                          <button
                            className={`${styles.campaignCard} ${
                              isCampaignSelected
                                ? styles.campaignCardSelected
                                : ''
                            } ${
                              isAlreadyLinked
                                ? styles.campaignCardAlreadyLinked
                                : ''
                            }`}
                            disabled={isAlreadyLinked}
                            key={campaign.id}
                            onClick={() => onToggleCampaign(campaign.id)}
                            type="button"
                          >
                            <div className={styles.campaignCardLeft}>
                              {isCampaignSelected ? (
                                <span className={styles.selectedCheckbox}>
                                  <DoubleCheckIcon
                                    className={styles.selectedCheckboxIcon}
                                  />
                                </span>
                              ) : (
                                <CheckboxInactiveIcon
                                  className={styles.campaignCheckboxIcon}
                                />
                              )}

                              <div className={styles.campaignCardText}>
                                <span className={styles.campaignCardTitle}>
                                  {campaign.title}
                                </span>
                                <span className={styles.campaignCardCategory}>
                                  {campaign.category}
                                </span>
                              </div>
                            </div>

                            <div className={styles.campaignMentions}>
                              {isAlreadyLinked ? (
                                <span className={styles.alreadyAddedBadge}>
                                  Already added
                                </span>
                              ) : (
                                <>
                                  <span
                                    className={styles.campaignMentionsValue}
                                  >
                                    {campaign.mentions}
                                  </span>
                                  <span
                                    className={styles.campaignMentionsLabel}
                                  >
                                    {getCampaignUnitLabel(
                                      campaign.sourceId,
                                      campaign.mentionsCount
                                    )}
                                  </span>
                                </>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </ScrollFadeList>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
